/* Live preview of the surname on the garment back (surname-back block).
   Mirrors the block's line-item-property input, uppercased, onto a picture of
   the back, with the font auto-shrinking so a long name never spills out of
   the print box.

   Where the name is drawn depends on what the page offers. On a product page
   there is a gallery holding the same back photo, so the overlay is mounted in
   the gallery media whose data-media-id matches and the carousel is switched to
   that slide while the student types - a big picture of the garment beats a
   small one beside the field, and the inline <surname-preview> figure hides
   itself. In the quick buy drawer there is no gallery, so that inline figure
   carries the name instead. It is the whole preview there, not a fallback.

   The chosen size used to be drawn here too, as a chip in the corner of the
   same image. It is rendered by snippets/product-gallery.liquid now, in Liquid,
   so that it exists on every product rather than the three that carry a name,
   and on every slide rather than the back.

   Text colour follows the selected garment colour: dark colourways get white
   text, light ones black. The selected colour is read from the variant JSON
   the theme keeps in the variant picker, so it survives option renames. */
(function () {
  "use strict";

  var DARK_COLORS = ["navy", "marine", "svart", "black", "mørkeblå", "morkebla", "mørk"];

  function isDark(value) {
    var v = String(value || "").toLowerCase();
    return DARK_COLORS.some(function (c) {
      return v.indexOf(c) !== -1;
    });
  }

  /* One surname, carried from an element being torn down to the one that
     replaces it. The quick buy drawer has no partial rerender: picking a size
     or a colour swaps the entire drawer content for a freshly rendered copy,
     and a name typed before the size was chosen would go in the bin with the
     old markup. The theme carries the quantity selector's value across its own
     rerender for exactly this reason.

     Deliberately one value on a zero timeout rather than a store. The
     replacement connects in the same task as the removal, so it is there for
     the swap and gone for everything else. That matters: a name reappearing in
     a drawer opened later, for the sibling's garment, would be a quiet and
     expensive mistake. */
  var carried = null;
  var carriedTimer = null;

  function carry(key, value) {
    carried = { key: key, value: value };
    clearTimeout(carriedTimer);
    carriedTimer = setTimeout(function () {
      carried = null;
    }, 0);
  }

  function takeCarried(key) {
    return carried && carried.key === key ? carried.value : "";
  }

  class SurnamePreview extends HTMLElement {
    connectedCallback() {
      this.inlineText = this.querySelector("[data-surname-text]");
      var formId = this.getAttribute("data-form");
      var property = this.getAttribute("data-property");
      var selector = 'input[name="properties[' + property + ']"]';
      this.input =
        (formId && document.querySelector(selector + '[form="' + formId + '"]')) ||
        document.querySelector(selector);
      /* The block ships this element hidden when it expects the overlay to end
         up in the gallery instead (see snippets/surname-back.liquid). That is a
         bet on this callback getting as far as mountOverlay, so if it does not,
         hand the attribute back rather than leaving a preview that no longer
         exists in either place. */
      if (!this.input || !this.inlineText) {
        this.removeAttribute("hidden");
        return;
      }

      // Same field, same product, across a drawer swap. See carry().
      this.carryKey = formId + "|" + property;
      if (!this.input.value) this.input.value = takeCarried(this.carryKey);

      this.placeholder = this.getAttribute("data-placeholder") || "ETTERNAVN";
      this.onInput = this.onInput.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onFormChange = this.onFormChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.mountOverlay();

      this.input.addEventListener("input", this.onInput);
      this.input.addEventListener("focus", this.onFocus);
      window.addEventListener("resize", this.onInput);
      // Variant switches fire change events on the picker inputs, which are
      // associated with the form but can live outside it in the DOM, so listen
      // at the document level.
      document.addEventListener("change", this.onFormChange);
      /* Capture phase, so it runs before the theme's own submit listener on
         the form builds its FormData (ProductForm._onSubmit in theme.js). */
      document.addEventListener("submit", this.onSubmit, true);

      this.onInput();

      // The first measurement can land before Bebas Neue arrives, which would
      // size the name against the fallback face and leave it too wide once the
      // real one swaps in. Re-fit when the webfont is ready.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.onInput).catch(function () {});
      }
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener("input", this.onInput);
        this.input.removeEventListener("focus", this.onFocus);
        if (this.input.value) carry(this.carryKey, this.input.value);
      }
      window.removeEventListener("resize", this.onInput);
      document.removeEventListener("change", this.onFormChange);
      document.removeEventListener("submit", this.onSubmit, true);
      // Mounted in the gallery, which outlives this element, so it has to be
      // taken back down or a second mount leaves a name behind.
      if (this.overlay) this.overlay.remove();
    }

    /* "farge=media_id,farge=media_id" from the block, one entry per colourway
       that has a back photo. Empty on products with a single colour. */
    backMediaMap() {
      if (this._backMap) return this._backMap;
      var map = {};
      var raw = this.getAttribute("data-back-media-map") || "";
      raw.split(",").forEach(function (pair) {
        var i = pair.indexOf("=");
        if (i > 0) map[pair.slice(0, i).trim().toLowerCase()] = pair.slice(i + 1).trim();
      });
      this._backMap = map;
      return map;
    }

    /* The back image for the colour currently selected, falling back to the
       one the block rendered with. Without this the overlay stays pinned to the
       first colourway's back and the name vanishes on every other colour,
       because that slide is hidden. */
    backMediaId() {
      var map = this.backMediaMap();
      var colour = String(this.currentColor() || "").toLowerCase();
      return map[colour] || this.getAttribute("data-back-media-id");
    }

    /* Mount the overlay span inside the gallery media that shows the back
       image, and hand the preview to the inline figure when there is no such
       gallery - which is the normal case in a quick buy drawer. */
    mountOverlay() {
      var mediaId = this.backMediaId();
      var target = null;
      if (mediaId) {
        /* Only this product's own gallery. A page can hold someone else's:
           a featured product section above a collection of cards, each card
           with a drawer. product-rerender is the unit the theme rerenders as
           one, so it is also the unit that belongs to one product - it wraps
           gallery and form together on a product page, and the form alone in
           a drawer. */
        var scope = this.closest("product-rerender") || document;
        target = scope.querySelector('.product-gallery__media[data-media-id="' + mediaId + '"]');
      }
      /* Record what was looked for even when it was not found, so targets()
         knows the question has been asked and does not go hunting for a
         gallery on every keystroke in a drawer, where the answer is always no. */
      this.mountedMediaId = mediaId;
      if (!target) {
        this.overlay = null;
        this.overlayMedia = null;
        this.removeAttribute("hidden");
        return;
      }
      target.style.position = "relative";
      var span = document.createElement("span");
      span.className = "surname-back__name is-placeholder";
      span.textContent = this.placeholder;
      target.appendChild(span);
      this.overlay = span;

      this.overlayMedia = target;
      this.setAttribute("hidden", "hidden");
    }

    /* The selected variant, read from THIS product's picker.

       Not a bare document.querySelector("script[data-variant]"): a collection
       page ends up with several, because every quick buy drawer keeps its
       content once it has been opened, and the first one in the document is
       rarely the one being looked at. Reading the wrong one paints the name
       white on a grey garment. The picker carries the form id the block was
       rendered with, which is the one thing tying the two together. */
    variantData() {
      var formId = this.getAttribute("data-form");
      var picker = formId && document.querySelector('variant-picker[form-id="' + formId + '"]');
      var script = picker && picker.querySelector("script[data-variant]");
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return null;
      }
    }

    targets() {
      // Re-mount if a section rerender threw the overlay away, or if the
      // shopper switched to a colour whose back photo is a different slide.
      var wanted = this.backMediaId();
      var stale = this.mountedMediaId !== wanted || (this.overlay && !this.overlay.isConnected);
      if (stale) {
        if (this.overlay) this.overlay.remove();
        this.overlay = null;
        this.mountOverlay();
      }
      return this.overlay ? [this.overlay] : [this.inlineText];
    }

    onFocus() {
      this.showBack();
    }

    /* Bring the gallery to the slide the name is printed on. */
    showBack(animate) {
      if (!this.overlayMedia) return;
      var carousel = this.overlayMedia.closest("media-carousel");
      if (!carousel || typeof carousel.select !== "function") return;
      var items = Array.prototype.slice.call(
        carousel.querySelectorAll(".product-gallery__media:not([hidden])")
      );
      var index = items.indexOf(this.overlayMedia);
      if (index >= 0) {
        try {
          carousel.select(index, { animate: animate !== false });
        } catch (e) {}
      }
    }

    onFormChange() {
      var self = this;
      // Give the theme's partial rerender a beat to settle first.
      setTimeout(function () {
        self.onInput();
        /* Picking a colour points the gallery at that variant's featured image,
           which is the front, so a shopper who had typed their name and was
           looking at the back gets bounced to the front of the other colourway.
           Once a name is in the field the back is the whole point of the
           preview, so go back to it. Only then: with the field empty there is
           nothing to see and the front is the right first impression. */
        /* Without animate:false the shopper watches the gallery scroll from the
           new colour's front to its back every time they change colour, because
           the variant picker has already moved it to the featured image by the
           time this runs. Jumping straight there reads as the back simply
           staying put. The smooth scroll is kept for focusing the field, where
           it is the thing that shows you where the name will appear. */
        if (self.input && self.input.value.trim()) self.showBack(false);
      }, 120);
    }

    currentColor() {
      var variant = this.variantData();
      if (!variant) return "";
      var options = [variant.option1, variant.option2, variant.option3];
      // Prefer whichever option the block knows a back photo for: that is by
      // definition the colour option, whatever position it sits in.
      var map = this.backMediaMap();
      for (var j = 0; j < options.length; j++) {
        if (options[j] && map[String(options[j]).toLowerCase()]) return options[j];
      }
      for (var i = 0; i < options.length; i++) {
        if (options[i] && isDark(options[i])) return options[i];
      }
      return options[0] || "";
    }

    /* What the preview draws is upper.trim(), but the form sent the field as
       typed, so "  BJØRNSTAD" reached the order with its two spaces - and, the
       field being 18 characters long, with the two spaces having pushed the
       end of the name off it. Straighten the value in the field itself before
       the form reads it: no space at either end, one space between names. */
    onSubmit(event) {
      if (!this.input || event.target !== this.input.form) return;
      var tidy = this.input.value.toUpperCase().trim().replace(/\s+/g, " ");
      if (tidy !== this.input.value) this.input.value = tidy;
    }

    onInput() {
      var raw = this.input.value;
      var upper = raw.toUpperCase();
      if (raw !== upper) {
        var pos = this.input.selectionStart;
        this.input.value = upper;
        if (pos !== null) this.input.setSelectionRange(pos, pos);
      }

      /* The "0/18" under the field (snippets/surname-back.liquid). Looked up
         each time rather than held: the drawer rebuilds the field with the
         picker on every option change, and the count belongs to whichever
         hint currently sits beside this input. */
      var block = this.input.closest(".surname-back");
      var count = block && block.querySelector("[data-surname-count]");
      if (count) count.textContent = String(upper.length);

      var name = upper.trim();
      var dark = isDark(this.currentColor());
      var els = this.targets();

      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        el.textContent = name || this.placeholder;
        el.classList.toggle("is-placeholder", name === "");
        el.classList.toggle("is-light", dark);
        this.fit(el);
      }
    }

    /* Shrink the type until the name fits the print box.

       scrollWidth cannot be used for this. The name is centred in a flex box
       with overflow visible, so a long one spills equally past both edges and
       scrollWidth reports only the right-hand half of the overflow, or nothing
       at all. Names then rendered straight off the garment onto the background.
       Measuring the glyphs on a canvas instead is exact. CSS applies
       letter-spacing after every character including the last, which
       measureText does not, so add it back. Both the glyph run and the tracking
       scale linearly with font-size, which makes the correction a single ratio
       rather than a search. */
    fit(el) {
      var boxWidth = el.clientWidth;
      var boxHeight = el.clientHeight;
      if (!boxWidth || !boxHeight) return;

      var size = boxHeight;
      el.style.fontSize = size + "px";

      var text = el.textContent || "";
      if (!text) return;

      var style = window.getComputedStyle(el);
      var ctx =
        SurnamePreview.measureContext ||
        (SurnamePreview.measureContext = document.createElement("canvas").getContext("2d"));
      ctx.font = style.fontStyle + " " + style.fontWeight + " " + size + "px " + style.fontFamily;

      var width = ctx.measureText(text).width + (parseFloat(style.letterSpacing) || 0) * text.length;
      // A hair of margin so the longest name never touches the seam.
      var limit = boxWidth * 0.98;
      if (width > limit) {
        el.style.fontSize = Math.max(6, (size * limit) / width) + "px";
      }
    }
  }

  if (!customElements.get("surname-preview")) {
    customElements.define("surname-preview", SurnamePreview);
  }
})();
